import { useRef, useState } from 'react';
import { ApiError, api, type SessionUser } from '../api';
import { ar, errorMessage } from '../ar';
import { IconShield } from '../components/icons';
import { Button } from '../components/primitives';

/**
 * A single quiet card on the application ground. No hero, no illustration — the first
 * impression of a serious operational product is that it gets out of the way.
 */
export function LoginScreen({
  onSignedIn,
}: {
  onSignedIn: (token: string, user: SessionUser, expiresAt: string) => void;
}) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (inFlight.current) return;
    if (!username.trim() || !password) {
      setError(ar.errInvalidCredentials);
      return;
    }

    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await api.login(username.trim(), password);
      if (result.user.role !== 'MANAGEMENT_ADMIN') {
        setError(ar.errForbidden);
        return;
      }
      setPassword('');
      onSignedIn(result.token, result.user, result.expiresAt);
    } catch (caught) {
      setError(errorMessage(caught instanceof ApiError ? caught.code : 'NETWORK'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <main className="login">
      <div className="login-card">
        <div className="login-brand">
          <span className="login-mark" aria-hidden="true">
            <IconShield size={19} />
          </span>
          <div className="login-brand-text">
            <p className="login-name">ROOM VERIFY</p>
            <p className="login-tagline">{ar.tagline}</p>
          </div>
        </div>

        <div className="login-head">
          <h1 className="login-title">{ar.signIn}</h1>
          <p className="login-sub">{ar.signInSubtitle}</p>
        </div>

        <form className="login-form" onSubmit={submit} noValidate>
          <label className="field">
            <span className="field-label">{ar.username}</span>
            <input
              className="input"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder={ar.usernamePlaceholder}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              dir="ltr"
              aria-invalid={error ? true : undefined}
            />
          </label>

          <label className="field">
            <span className="field-label">{ar.password}</span>
            <span className="input-affix">
              <input
                className="input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder={ar.passwordPlaceholder}
                autoComplete="current-password"
                dir="ltr"
                aria-invalid={error ? true : undefined}
              />
              <button
                type="button"
                className="input-affix-btn"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? ar.hidePassword : ar.showPassword}
              >
                {showPassword ? ar.hidePassword : ar.showPassword}
              </button>
            </span>
          </label>

          {error && (
            <p className="field-error" role="alert">
              {error}
            </p>
          )}

          <Button type="submit" variant="accent" size="lg" block loading={busy}>
            {busy ? ar.signingIn : ar.signIn}
          </Button>
        </form>
      </div>
    </main>
  );
}
